import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLang } from "@/i18n/LanguageContext";
import { Kicker, SectionHead } from "@/components/landing/Section";

export const CityBand = () => {
  const { t } = useLang();
  const s = t.pages.supporter;
  return (
    <section data-testid="supporter-city-band" className="relative overflow-hidden rounded-[2rem] bg-ink text-parchment px-6 py-12 sm:px-10 md:px-16 md:py-20">
      <img src="/trolls/nordlysdronninga.webp" alt="" className="hidden lg:block absolute right-8 bottom-0 h-[88%] max-w-[34%] object-contain object-bottom pointer-events-none" />
      <div className="relative max-w-3xl lg:max-w-[60%]">
        <Kicker className="!text-amber">{s.cityKicker}</Kicker>
        <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1]">{s.cityTitle}</h2>
        {s.cityBody.map((p) => (
          <p key={p.slice(0, 24)} className="mt-5 text-base md:text-lg text-parchment/80 leading-relaxed">{p}</p>
        ))}
        <ul className="mt-8 grid sm:grid-cols-2 gap-3">
          {s.cityPoints.map((pt) => (
            <li key={pt} className="flex gap-3 rounded-2xl bg-parchment/5 border border-parchment/10 px-4 py-3 font-bold">
              <Check className="w-5 h-5 text-amber shrink-0" />
              {pt}
            </li>
          ))}
        </ul>
        <Link to={{ search: "?role=municipality&package=region", hash: "#signup" }} data-testid="supporter-city-cta" className="mt-10 inline-flex items-center gap-2 rounded-full bg-amber hover:bg-amber-dark text-parchment px-7 py-3.5 font-extrabold transition-[background-color,transform] duration-200 hover:-translate-y-0.5">
          {t.pages.signup.launchCta}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
};

export const Steps = () => {
  const { t } = useLang();
  const s = t.pages.supporter;
  return (
    <section data-testid="supporter-steps">
      <SectionHead kicker={s.stepsKicker} title={s.stepsTitle} />
      <ol className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {s.steps.map((step, i) => (
          <motion.li
            key={step.h}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.45, delay: i * 0.1 }}
            className="relative border-t-2 border-dashed border-line pt-6"
          >
            <span className="font-display text-5xl font-semibold text-amber/80 tabular-nums">0{i + 1}</span>
            <h3 className="mt-3 font-display text-xl font-semibold text-ink">{step.h}</h3>
            <p className="mt-2 text-ink-muted leading-relaxed">{step.p}</p>
          </motion.li>
        ))}
      </ol>
    </section>
  );
};

export const SupporterFAQ = () => {
  const { t } = useLang();
  const s = t.pages.supporter;
  return (
    <section data-testid="supporter-faq" className="grid lg:grid-cols-12 gap-10">
      <h2 className="lg:col-span-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink leading-[1.1]">{s.faqTitle}</h2>
      <Accordion type="single" collapsible className="lg:col-span-8">
        {s.faq.map(([q, a], i) => (
          <AccordionItem key={q} value={`s${i}`} className="border-b border-dashed border-line">
            <AccordionTrigger data-testid={`supporter-faq-trigger-${i}`} className="font-display text-lg md:text-xl font-semibold text-ink text-left hover:no-underline py-5">
              {q}
            </AccordionTrigger>
            <AccordionContent className="text-base text-ink-muted leading-relaxed pb-5">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
};

export const FinalCta = () => {
  const { t } = useLang();
  const s = t.pages.supporter;
  return (
    <section data-testid="supporter-final-cta" className="journal-card bg-paper/70 p-8 md:p-14 flex flex-col md:flex-row md:items-center justify-between gap-8">
      <div className="max-w-2xl">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink leading-[1.1]">{s.finalTitle}</h2>
        <p className="mt-4 text-base md:text-lg text-ink-muted leading-relaxed">{s.finalBody}</p>
      </div>
      <Link to="/contact?package=general" data-testid="supporter-final-contact" className="shrink-0 inline-flex items-center gap-2 rounded-full bg-moss hover:bg-moss-dark text-parchment px-8 py-4 text-lg font-extrabold shadow-journal transition-[background-color,transform] duration-200 hover:-translate-y-0.5">
        {s.finalCta}
        <ArrowRight className="w-5 h-5" />
      </Link>
    </section>
  );
};
